import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { pageTitle, BRAND_NAME } from "@/brand";
import SitewrapLogo from "@/components/SitewrapLogo";
import "@/styles/sitewrap-logo.css";
import { dashFetch } from "@/api/client";
import AssistantBridge from "@/assistant/AssistantBridge";
import AssistantChat from "@/assistant/AssistantChat";
import AssistantTaskModal from "@/assistant/AssistantTaskModal";
import AssistantTaskWidget from "@/assistant/AssistantTaskWidget";
import { useAssistantTasks } from "@/assistant/AssistantTaskContext";
import "@/styles/assistant-tasks.css";
import DashLoader, { DashUserSkeleton } from "@/components/DashLoader";
import SiteSwitcher from "@/components/SiteSwitcher";
import { useDashboard } from "@/context/DashboardContext";
import { DASHBOARD_TABS, type DashboardTab } from "@/types/dashboard";
import {
  DASHBOARD_NAV_SECTIONS,
  DASHBOARD_PANEL_META,
  defaultPanelForSection,
  sectionForTab,
  type NavSection
} from "@/nav/navConfig";
import { NavTabIcon } from "@/nav/NavTabIcon";
import { buildSiteNotifications, notificationMetaLine, truncateText } from "@/utils/notifications";

const HomeTab = lazy(() => import("@/features/home/HomeTab"));
const RequestLogTab = lazy(() => import("@/features/request-log/RequestLogTab"));
const AuditLogTab = lazy(() => import("@/features/audit-log/AuditLogTab"));
const ComplianceTab = lazy(() => import("@/features/compliance/ComplianceTab"));
const CacheTab = lazy(() => import("@/features/cache/CacheTab"));
const BillingTab = lazy(() => import("@/features/billing/BillingTab"));
const FirewallTab = lazy(() => import("@/features/firewall/FirewallTab"));
const PreloadTab = lazy(() => import("@/features/preload/PreloadTab"));
const LiveUsersTab = lazy(() => import("@/features/live-users/LiveUsersTab"));
const CookiesTab = lazy(() => import("@/features/cookies/CookiesTab"));
const FormsTab = lazy(() => import("@/features/forms/FormsTab"));
const RedirectsTab = lazy(() => import("@/features/redirects/RedirectsTab"));
const QueueTab = lazy(() => import("@/features/queue/QueueTab"));
const LeadsTab = lazy(() => import("@/features/leads/LeadsTab"));
const DeployTab = lazy(() => import("@/features/deploy/DeployTab"));
const PagesTab = lazy(() => import("@/features/pages/PagesTab"));
const RecordingsTab = lazy(() => import("@/features/recordings/RecordingsTab"));
const SettingsTab = lazy(() => import("@/features/settings/SettingsTab"));
const AbTestsTab = lazy(() => import("@/features/ab-tests/AbTestsTab"));

function TabPanel({ tab, active, children }: { tab: DashboardTab; active: boolean; children: React.ReactNode }) {
  return (
    <div className={"tabPanel" + (active ? " active" : "")} data-panel={tab} role="tabpanel">
      {active ? children : null}
    </div>
  );
}

function tabBadgeCount(
  badge: "live" | "leads" | "deploy" | undefined,
  status: Record<string, unknown> | null,
  deployRunning: boolean
): { text: string; on: boolean } {
  if (badge === "live") {
    const sockets = status?.sockets as Record<string, unknown> | undefined;
    const n = Number(sockets?.registeredUsers || 0) || 0;
    return { text: String(n), on: n > 0 };
  }
  if (badge === "leads") {
    const leads = status?.leads as Record<string, unknown> | undefined;
    const n = Number(leads?.unhandled || 0) || 0;
    return { text: String(n), on: n > 0 };
  }
  if (badge === "deploy") {
    return { text: deployRunning ? "●" : "", on: deployRunning };
  }
  return { text: "", on: false };
}

function ActiveTabContent({ tab }: { tab: DashboardTab }) {
  switch (tab) {
    case "home": return <HomeTab />;
    case "request-log": return <RequestLogTab />;
    case "audit-log": return <AuditLogTab />;
    case "compliance": return <ComplianceTab />;
    case "cache": return <CacheTab />;
    case "billing": return <BillingTab />;
    case "firewall": return <FirewallTab />;
    case "preload": return <PreloadTab />;
    case "live-users": return <LiveUsersTab />;
    case "cookies": return <CookiesTab />;
    case "forms": return <FormsTab />;
    case "redirects": return <RedirectsTab />;
    case "queue": return <QueueTab />;
    case "leads": return <LeadsTab />;
    case "deploy": return <DeployTab />;
    case "site-structure": return <PagesTab />;
    case "ab-tests": return <AbTestsTab />;
    case "recordings": return <RecordingsTab />;
    case "settings": return <SettingsTab />;
    default: return <HomeTab />;
  }
}

const TAB_CAPABILITY: Partial<Record<DashboardTab, string>> = {
  home: "home",
  "request-log": "request_log",
  "audit-log": "audit_log",
  compliance: "compliance",
  cache: "site_usage",
  preload: "preload",
  "live-users": "live_users",
  recordings: "recordings",
  cookies: "cookies",
  forms: "forms",
  leads: "leads",
  redirects: "redirects",
  queue: "queue",
  "site-structure": "pages",
  "ab-tests": "ab_tests",
  deploy: "deploy",
  billing: "billing",
  firewall: "firewall",
  settings: "settings"
};

function tabCapability(tab: DashboardTab): string {
  return TAB_CAPABILITY[tab] || tab;
}

function sectionBadge(
  section: NavSection,
  status: Record<string, unknown> | null,
  deployRunning: boolean
): { text: string; on: boolean } {
  if (!section.badgeFrom?.length) return { text: "", on: false };
  for (const kind of section.badgeFrom) {
    const b = tabBadgeCount(kind, status, deployRunning);
    if (b.on) return b;
  }
  return { text: "", on: false };
}

export default function DashboardShell() {
  const {
    activeSite,
    controlPlaneEnabled,
    status,
    user,
    activeTab,
    setActiveTab,
    deployRunning,
    logs,
    loadLogs,
    tenants
  } = useDashboard();

  const activeTenant = useMemo(
    () => tenants.find((t) => String(t.domain || "") === activeSite),
    [tenants, activeSite]
  );
  const activationPending =
    String(activeTenant?.status || "active") === "pending" &&
    Boolean(activeTenant?.dashboardPreview);

  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [upgradeTab, setUpgradeTab] = useState<DashboardTab | null>(null);
  const [lastPanelBySection, setLastPanelBySection] = useState<Partial<Record<string, DashboardTab>>>({});
  const userWrapRef = useRef<HTMLDivElement>(null);
  const notifWrapRef = useRef<HTMLDivElement>(null);

  const isAdmin = Boolean(user.isAdministrator);
  const activeCapabilities = activeTenant?.capabilities || {};
  const activePlanName = activeTenant?.planName || activeTenant?.planId || "current";
  const canUseTab = useCallback((tab: DashboardTab) => {
    if (isAdmin) return true;
    if (!activeTenant) return true;
    const cap = tabCapability(tab);
    return activeCapabilities[cap] === true;
  }, [activeCapabilities, activeTenant, isAdmin]);

  const activeSection = useMemo(() => sectionForTab(activeTab), [activeTab]);
  const secondaryPanels = activeSection.panels;
  const showSecondary = secondaryPanels.length > 1;

  const notifications = useMemo(
    () => buildSiteNotifications(status, logs, activeSite),
    [status, logs, activeSite]
  );
  const importantCount = notifications.filter((x) => x.kind === "bad" || x.kind === "warn").length;

  const accountHref = controlPlaneEnabled && activeSite
    ? `/dashboard/account?site=${encodeURIComponent(activeSite)}`
    : "/dashboard/account";

  const { tasks: assistantTasks, focusedTask, modalOpen: taskModalOpen, openTask, closeModal: closeTaskModal } =
    useAssistantTasks();

  const userInitial = (user.email || "U").charAt(0).toUpperCase();

  const handleLogout = useCallback(async () => {
    try {
      await dashFetch("/dashboard/api/logout", { method: "POST" });
      location.href = "/dashboard/login";
    } catch {
      location.href = "/dashboard/login";
    }
  }, []);

  const openPanel = useCallback((tab: DashboardTab) => {
    if (!canUseTab(tab)) {
      setUpgradeTab(tab);
      return;
    }
    const section = sectionForTab(tab);
    setLastPanelBySection((prev) => ({ ...prev, [section.id]: tab }));
    setActiveTab(tab);
  }, [canUseTab, setActiveTab]);

  const openSection = useCallback((section: NavSection) => {
    const remembered = lastPanelBySection[section.id];
    const preferred =
      remembered && section.panels.includes(remembered) && canUseTab(remembered)
        ? remembered
        : defaultPanelForSection(section, canUseTab);
    if (!canUseTab(preferred)) {
      setUpgradeTab(preferred);
      return;
    }
    setLastPanelBySection((prev) => ({ ...prev, [section.id]: preferred }));
    setActiveTab(preferred);
  }, [canUseTab, lastPanelBySection, setActiveTab]);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (userWrapRef.current && !userWrapRef.current.contains(target)) setUserMenuOpen(false);
      if (notifWrapRef.current && !notifWrapRef.current.contains(target)) setNotifOpen(false);
    };
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, []);

  useEffect(() => {
    document.title = pageTitle(activeSite || "Dashboard");
  }, [activeSite]);

  useEffect(() => {
    if (activeTab !== "home" && !canUseTab(activeTab)) {
      setUpgradeTab(activeTab);
      setActiveTab("home");
    }
  }, [activeTab, canUseTab, setActiveTab]);

  useEffect(() => {
    const section = sectionForTab(activeTab);
    setLastPanelBySection((prev) =>
      prev[section.id] === activeTab ? prev : { ...prev, [section.id]: activeTab }
    );
  }, [activeTab]);

  useEffect(() => {
    if (notifOpen && logs.length === 0) void loadLogs({ reset: true, filters: null });
  }, [notifOpen, logs.length, loadLogs]);

  return (
    <div className="wrap dashShell">
      <div className="topbar">
        <div className="leftSide">
          <SitewrapLogo
            className="dashBrand"
            size={26}
            variant="gradient"
            href={accountHref}
            title={`${BRAND_NAME} account`}
          />
          <a className="dashHomeBtn" href={accountHref} aria-label="Account home" title="Account home">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M3 10.5 12 3l9 7.5" />
              <path d="M5 10v10a1 1 0 0 0 1 1h4v-6h4v6h4a1 1 0 0 0 1-1V10" />
            </svg>
          </a>
          <SiteSwitcher />
          {activationPending ? (
            <span className="siteActivationPill" role="status">
              DNS activation pending
            </span>
          ) : null}
        </div>
        <div className="rightSide">
          <AssistantTaskWidget
            tasks={assistantTasks.filter(
              (t) =>
                t.status === "running" ||
                (typeof t.finishedAtMs === "number" && t.finishedAtMs > Date.now() - 3600000)
            )}
            onOpen={openTask}
          />
          <div className="notifWrap" ref={notifWrapRef}>
            <button
              className={"notifBtn" + (notifOpen ? " on" : "")}
              type="button"
              title="Notifications"
              aria-label="Notifications"
              aria-haspopup="menu"
              aria-expanded={notifOpen}
              onClick={(e) => {
                e.stopPropagation();
                setUserMenuOpen(false);
                setNotifOpen((v) => !v);
              }}
            >
              <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M15 17H9m10-1.5c-.9-1.1-1.5-2.6-1.5-4.6V9a5.5 5.5 0 0 0-11 0v1.9c0 2-.6 3.5-1.5 4.6-.5.6-.1 1.5.7 1.5h12.6c.8 0 1.2-.9.7-1.5Z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M10 20a2.2 2.2 0 0 0 4 0" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
              </svg>
              <span className={"notifCount" + (importantCount > 0 ? " on" : "")}>
                {Math.min(importantCount, 99)}
              </span>
            </button>
            <div className={"notifPanel" + (notifOpen ? " on" : "")} role="menu" aria-label="Latest notifications">
              <div className="notifHead">
                <div>
                  <strong>Notifications</strong>
                  <br />
                  <span>{activeSite || "Current site"} · latest notifications</span>
                </div>
                <button
                  className="btn"
                  type="button"
                  style={{ padding: "6px 9px", fontSize: 11 }}
                  onClick={() => void loadLogs({ reset: true })}
                >
                  Refresh
                </button>
              </div>
              <div className="notifList">
                {notifications.length === 0 ? (
                  <div className="notifEmpty">All clear. Important site events will appear here.</div>
                ) : (
                  notifications.map((it, i) => (
                    <div key={`${it.ts}-${i}`} className={"notifItem " + it.kind} role="menuitem">
                      <span className="notifMark" aria-hidden="true" />
                      <div>
                        <div className="notifTitle">{it.title}</div>
                        <div className="notifText">{truncateText(it.text, 150)}</div>
                        <div className="notifMeta">{notificationMetaLine(it)}</div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
          <div className="userWrap" ref={userWrapRef}>
            <button
              className="userBtn"
              type="button"
              aria-haspopup="menu"
              aria-expanded={userMenuOpen}
              onClick={(e) => {
                e.stopPropagation();
                setNotifOpen(false);
                setUserMenuOpen((v) => !v);
              }}
            >
              {user.email ? (
                <>
                  <span className="avatar">{userInitial}</span>
                  <span className="userMeta">
                    <span className="userEmail">{user.email}</span>
                    <span className="userRole">{user.role || "—"}</span>
                  </span>
                </>
              ) : (
                <DashUserSkeleton />
              )}
            </button>
            <div className={"userMenu" + (userMenuOpen ? " on" : "")} role="menu" aria-label="User menu">
              <div className="userMenuHeader">
                <div className="mono muted">Signed in</div>
                <div className="mono">{user.email || "—"}</div>
              </div>
              <a className="userMenuItem" href={accountHref} role="menuitem" onClick={() => setUserMenuOpen(false)}>Account home</a>
              <a className="userMenuItem" href={`${accountHref}#profile`} role="menuitem" onClick={() => setUserMenuOpen(false)}>Profile</a>
              <a className="userMenuItem" href={`${accountHref}#billing`} role="menuitem" onClick={() => setUserMenuOpen(false)}>Billing</a>
              <button className="userMenuItem danger" type="button" role="menuitem" onClick={() => void handleLogout()}>
                Log out
              </button>
            </div>
          </div>
        </div>
      </div>

      <nav className="navPrimary" aria-label="Primary">
        <div className="tabs tabsPrimary" role="tablist" aria-label="Site sections">
          {DASHBOARD_NAV_SECTIONS.map((section) => {
            const isActive = activeSection.id === section.id;
            const badge = sectionBadge(section, status as Record<string, unknown> | null, deployRunning);
            const sectionLocked = section.panels.every((p) => !canUseTab(p));
            return (
              <button
                key={section.id}
                className={"tab tabPrimary" + (isActive ? " active" : "")}
                type="button"
                role="tab"
                aria-selected={isActive}
                title={section.purpose}
                onClick={() => openSection(section)}
              >
                <span className="tabIcon" aria-hidden="true">
                  <NavTabIcon id={section.id} />
                </span>
                <span className="tabLabel">{section.label}</span>
                {sectionLocked ? <span className="tabLock" aria-hidden="true">Lock</span> : null}
                {section.badgeFrom ? (
                  <span
                    className={"tabBadge" + (badge.on ? " on" : "")}
                    title={section.badgeFrom.includes("deploy") && badge.on ? "Deployment in progress" : undefined}
                  >
                    {badge.text}
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
      </nav>

      {showSecondary ? (
        <div className="navSecondary" aria-label={`${activeSection.label} sections`}>
          <div className="tabs tabsSecondary" role="tablist">
            {secondaryPanels.map((panelId) => {
              const meta = DASHBOARD_PANEL_META[panelId];
              const isActive = activeTab === panelId;
              const badge = tabBadgeCount(meta.badge, status as Record<string, unknown> | null, deployRunning);
              return (
                <button
                  key={panelId}
                  className={"tab tabSecondary" + (isActive ? " active" : "")}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  data-tab={panelId}
                  title={meta.description}
                  onClick={() => openPanel(panelId)}
                >
                  {meta.label}
                  {!canUseTab(panelId) ? <span className="tabLock" aria-hidden="true">Lock</span> : null}
                  {meta.badge ? (
                    <span
                      className={"tabBadge" + (badge.on ? " on" : "")}
                      title={meta.badge === "deploy" ? "Deployment in progress" : undefined}
                    >
                      {badge.text}
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>
          <p className="navSectionHint muted">{activeSection.purpose}</p>
        </div>
      ) : null}

      <Suspense
        fallback={
          <div className="tabPanel active">
            <DashLoader size="page" label="Loading this view" hint="Preparing the dashboard panel" />
          </div>
        }
      >
        {DASHBOARD_TABS.map((t) => (
          <TabPanel key={t.id} tab={t.id} active={activeTab === t.id}>
            <ActiveTabContent tab={t.id} />
          </TabPanel>
        ))}
      </Suspense>
      {upgradeTab ? (
        <div className="upgradeOverlay" role="dialog" aria-modal="true" aria-label="Upgrade required" onClick={() => setUpgradeTab(null)}>
          <div className="upgradeModal" onClick={(e) => e.stopPropagation()}>
            <div className="upgradeIcon" aria-hidden="true">SW</div>
            <h3>
              Upgrade this website to unlock{" "}
              {DASHBOARD_PANEL_META[upgradeTab]?.label ||
                DASHBOARD_TABS.find((t) => t.id === upgradeTab)?.label ||
                "this feature"}
            </h3>
            <p>
              This feature is not included in the {activePlanName} plan for {activeSite || "this website"}.
              Upgrade the website plan to make this dashboard module and its actions available.
            </p>
            <div className="upgradeActions">
              <a className="btn primary" href={`/dashboard/account?site=${encodeURIComponent(activeSite || "")}#billing`}>View plans</a>
              <button type="button" className="btn" onClick={() => setUpgradeTab(null)}>Close</button>
            </div>
          </div>
        </div>
      ) : null}
      <AssistantBridge />
      <AssistantChat />
      <AssistantTaskModal
        open={taskModalOpen}
        task={focusedTask}
        onClose={closeTaskModal}
        onOpenPage={(path) => {
          closeTaskModal();
          openPanel("site-structure");
          window.setTimeout(() => {
            window.dispatchEvent(
              new CustomEvent("crea:assistant-pending", {
                detail: { type: "open_page_stats", path }
              })
            );
          }, 300);
        }}
      />
    </div>
  );
}
